import { Alert, Box, Button, Chip, CircularProgress, LinearProgress, SelectChangeEvent, Stack, Typography } from "@mui/material";
import Papa from "papaparse";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useModals } from "src/context/ModalContext";
import { BaseModalComponent } from "../BaseModalComponent";
import { ColumnMappingModalTrigger } from "./components/ColumnMappingModalTrigger";
import { ColumnMappingPanel } from "./components/ColumnMappingPanel";
import { DataPreviewTable } from "./components/DataPreviewTable";
import { ErrorPanel } from "./components/ErrorPanel";
import { DataImportCellError, DataImportColumnMapping, DataImportProps, DataImportStatus } from "./DataImport.interfaces";
import {
  buildInitialMappings,
  extractHeadersFromRows,
  getRequiredFieldsWithoutMapping,
  isManualMappingStatus,
  normalizePapaRows,
  validateMappedRows,
  resolveValueFromMultipleHeaders,
} from "./helpers/DataInporter.helpers";

export default function DataImport<TEntity extends Record<string, unknown>>({
  entityName,
  fields,
  maxPreviewRows = 10,
  allowPartialImport = false,
  onConfirm,
  isLoading = false,
  onReset,
  transformRow,
}: DataImportProps<TEntity>) {
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState<DataImportStatus>(DataImportStatus.Idle);
  const [fileName, setFileName] = useState("");
  const [page, setPage] = useState(0);

  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, unknown>[]>([]);
  const [mappings, setMappings] = useState<DataImportColumnMapping[]>([]);
  const [errors, setErrors] = useState<DataImportCellError[]>([]);
  const columnMappingModalKey = `${entityName}-DataImport-ColumnMapping`;

  const { closeByKey } = useModals();

  const columnMappingModalTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [autoOpenColumnMappingKey, setAutoOpenColumnMappingKey] = useState(0);

  const processFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setStatus(DataImportStatus.Invalid);
      setErrors([
        {
          rowIndex: -1,
          fieldKey: "file",
          fieldLabel: "File Format",
          message: "Invalid format. Only .csv files are allowed.",
        },
      ]);
      return;
    }

    setStatus(DataImportStatus.Parsing);
    setFileName(file.name);
    setPage(0);

    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      complete: (result) => {
        const parsedRows = normalizePapaRows(result.data);
        const headers = result.meta.fields ?? extractHeadersFromRows(parsedRows);
        const initialMappings = buildInitialMappings(fields, headers);
        const initialErrors = validateMappedRows(parsedRows, fields, initialMappings);

        const initialRequiredFieldsWithoutMapping = getRequiredFieldsWithoutMapping(fields, initialMappings);

        const hasInitialMappingProblems = initialRequiredFieldsWithoutMapping.length > 0 || initialErrors.length > 0;

        const hasInitialManualMappedColumns = initialMappings.some((mapping) => {
          return mapping.csvHeaders.length > 0 && isManualMappingStatus(mapping.matchedBy);
        });

        setCsvHeaders(headers);
        setRawRows(parsedRows);
        setMappings(initialMappings);
        setErrors(initialErrors);
        setStatus(initialErrors.length > 0 ? DataImportStatus.Invalid : DataImportStatus.Ready);

        if (hasInitialManualMappedColumns && !hasInitialMappingProblems) {
          setAutoOpenColumnMappingKey((currentValue) => currentValue + 1);
        }
      },
      error: () => {
        setStatus(DataImportStatus.Invalid);
      },
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isLoading) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (isLoading) return;

    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) processFile(file);
    if (event.target) event.target.value = "";
  };

  const mappedRows = useMemo(() => {
    return rawRows.map((rawRow) => {
      const mappedRow: Record<string, unknown> = {};

      fields.forEach((field) => {
        const mapping = mappings.find((item) => item.fieldKey === field.key);
        if (!mapping?.csvHeaders || mapping.csvHeaders.length === 0) {
          mappedRow[field.key] = undefined;
          return;
        }
        mappedRow[field.key] = resolveValueFromMultipleHeaders(rawRow, mapping.csvHeaders, field);
      });

      return transformRow ? transformRow(mappedRow) : (mappedRow as TEntity);
    });
  }, [rawRows, mappings, fields, transformRow]);

  const previewRows = useMemo(() => {
    const start = page * maxPreviewRows;
    return mappedRows.slice(start, start + maxPreviewRows);
  }, [mappedRows, page, maxPreviewRows]);

  const requiredFieldsWithoutMapping = useMemo(() => {
    return getRequiredFieldsWithoutMapping(fields, mappings);
  }, [fields, mappings]);

  const hasMappingProblems = requiredFieldsWithoutMapping.length > 0 || errors.length > 0;

  const hasManualMappedColumns = useMemo(() => {
    return mappings.some((mapping) => {
      return mapping.csvHeaders && mapping.csvHeaders.length > 0 && isManualMappingStatus(mapping.matchedBy);
    });
  }, [mappings]);

  const canContinue = mappedRows.length > 0 && requiredFieldsWithoutMapping.length === 0 && (allowPartialImport || errors.length === 0);

  useEffect(() => {
    if (autoOpenColumnMappingKey === 0) return;

    columnMappingModalTriggerRef.current?.click();
  }, [autoOpenColumnMappingKey]);

  const handleMappingChange = (fieldKey: string, event: SelectChangeEvent<string | string[]>) => {
    const value = event.target.value;
    const nextCsvHeaders = typeof value === "string" ? value.split(",").filter(Boolean) : value;

    const nextMappings = mappings.map((mapping) => {
      if (mapping.fieldKey !== fieldKey) return mapping;
      return {
        ...mapping,
        csvHeaders: nextCsvHeaders,
        confidence: nextCsvHeaders.length > 0 ? 1 : 0,
        matchedBy: "manual",
      };
    });

    const nextErrors = validateMappedRows(rawRows, fields, nextMappings);

    setMappings(nextMappings);
    setErrors(nextErrors);
    setStatus(nextErrors.length > 0 ? DataImportStatus.Invalid : DataImportStatus.Ready);
  };

  const handleReset = () => {
    setStatus(DataImportStatus.Idle);
    setFileName("");
    setCsvHeaders([]);
    setRawRows([]);
    setMappings([]);
    setErrors([]);
    setPage(0);
    setAutoOpenColumnMappingKey(0);
    onReset?.();
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        transition: "all 0.2s ease",
        border: isDragging ? "2px dashed #1976d2" : "2px dashed transparent",
        backgroundColor: isDragging ? "rgba(25, 118, 210, 0.04)" : "transparent",
        borderRadius: 2,
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Stack spacing={2.5}>
        {status === DataImportStatus.Idle && (
          <Box
            component="label"
            sx={{
              py: 6,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
              textAlign: "center",
              cursor: isLoading ? "default" : "pointer",
              "&:hover": { borderColor: isLoading ? "divider" : "primary.main", bgcolor: "rgba(25, 118, 210, 0.02)" },
              backgroundColor: "background.paper",
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Upload {entityName} CSV
            </Typography>
            <Typography variant="caption" sx={{ mb: 2, color: "text.secondary", px: 2, display: "block" }}>
              Drag and drop your file here or click to browse.
            </Typography>
            <Button variant="outlined" size="small" component="span" disabled={isLoading} disableElevation>
              Select File
            </Button>
            <input hidden type="file" accept=".csv" onChange={handleFileChange} disabled={isLoading} />
          </Box>
        )}

        {status === DataImportStatus.Parsing && <LinearProgress color="primary" sx={{ borderRadius: 1 }} />}

        {fileName && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ bgcolor: "background.paper", p: 1.5, borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}
          >
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {fileName}
              </Typography>
              {rawRows.length > 0 && (
                <Chip
                  label={`${rawRows.length} rows`}
                  size="small"
                  color={errors.length > 0 ? "error" : "success"}
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
            </Box>
            {rawRows.length > 0 && !isLoading && (
              <Button variant="text" size="small" color="primary" onClick={handleReset} sx={{ textTransform: "none" }}>
                Cancel & Reset
              </Button>
            )}
          </Stack>
        )}

        {/* Alerts */}
        {requiredFieldsWithoutMapping.length > 0 && (
          <Alert severity="warning" sx={{ borderRadius: 1.5, py: 0 }}>
            Missing required mappings: {requiredFieldsWithoutMapping.map((field) => field.label).join(", ")}
          </Alert>
        )}

        {errors.length > 0 && status !== DataImportStatus.Idle && (
          <Alert severity="error" sx={{ borderRadius: 1.5, py: 0 }}>
            Found {errors.length} validation error(s). Please review the table below.
          </Alert>
        )}

        {rawRows.length > 0 && (
          <Stack spacing={2}>
            <BaseModalComponent
              modalKey={columnMappingModalKey}
              modalLabel={`${entityName} Column Mapping`}
              elementTrigger={
                <ColumnMappingModalTrigger
                  ref={columnMappingModalTriggerRef}
                  entityName={entityName}
                  mappings={mappings}
                  errorsCount={errors.length}
                  requiredFieldsWithoutMappingCount={requiredFieldsWithoutMapping.length}
                  hasManualMappedColumns={hasManualMappedColumns}
                  hasMappingProblems={hasMappingProblems}
                />
              }
            >
              <Stack spacing={2}>
                <ColumnMappingPanel
                  fields={fields}
                  csvHeaders={csvHeaders}
                  mappings={mappings}
                  onMappingChange={handleMappingChange}
                  disabled={isLoading}
                />

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={() => closeByKey(columnMappingModalKey)}
                    sx={{
                      px: 4,
                      py: 1,
                      margin: "auto",
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      bgcolor: "#FF8A00",
                      color: "#fff",
                      boxShadow: "0 6px 16px rgba(255, 138, 0, 0.22)",
                      "&:hover": {
                        bgcolor: "#E67A00",
                        boxShadow: "0 8px 20px rgba(255, 138, 0, 0.28)",
                      },
                    }}
                  >
                    OK
                  </Button>
                </Stack>
              </Stack>
            </BaseModalComponent>

            <DataPreviewTable
              fields={fields}
              rows={previewRows}
              errors={errors}
              page={page}
              rowsCount={mappedRows.length}
              maxPreviewRows={maxPreviewRows}
              onPageChange={setPage}
            />

            <ErrorPanel errors={errors} />

            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                disableElevation
                disabled={!canContinue || isLoading}
                onClick={() => onConfirm(mappedRows as TEntity[])}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ borderRadius: 1.5, textTransform: "none", px: 4 }}
              >
                {isLoading ? "Processing..." : `Import ${rawRows.length} Records`}
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
